import { supabase, validateConnection, PRODUCTS_TABLE, handleSupabaseError, withRetry } from './supabase';
import { getLocalProducts, addLocalProduct, updateLocalProduct, deleteLocalProduct } from './localdb';
import type { Product, ProductInput, ProductUpdate } from './types';
import toast from 'react-hot-toast';
import { useAppContext } from './AppContext';
import React from 'react';

// Variable pour suivre si nous utilisons le mode local ou non
let useLocalMode = localStorage.getItem('demoMode') === 'true';

export async function initDb() {
  try {
    console.log('Initialisation de la base de données...');
    
    // Si le mode démo est activé, on utilise directement la base locale
    if (useLocalMode) {
      console.log('Mode hors-ligne activé, utilisation de la base de données locale');
      return true;
    }
    
    // Sinon, on essaie de se connecter à Supabase
    console.log('Tentative de connexion à Supabase...');
    const isConnected = await validateConnection();
    
    if (!isConnected) {
      console.log('Échec de la connexion à Supabase, passage en mode local');
      useLocalMode = true;
      localStorage.setItem('demoMode', 'true');
      toast.error('Erreur de connexion à la base de données. Mode hors-ligne activé.');
      return false;
    }
    
    console.log('Connexion à Supabase établie avec succès');
    toast.success('Connexion à la base de données établie');
    return true;
  } catch (error) {
    console.error('Erreur d\'initialisation de la base de données:', error);
    
    // En cas d'erreur, passer en mode local
    useLocalMode = true;
    localStorage.setItem('demoMode', 'true');
    toast.error('Erreur d\'initialisation de la base de données. Mode hors-ligne activé.');
    
    return false;
  }
}

export async function setUseLocalMode(value: boolean) {
  useLocalMode = value;
  localStorage.setItem('demoMode', value ? 'true' : 'false');
}

// Extrait le prix d'un item de manière robuste (ex: "test 12€", "note 12.50 €")
function parseItemPrice(item: string): number {
  const match = item.match(/(\d+(?:[.,]\d+)?)\s*€?\s*$/);
  if (!match) return 0;
  const price = parseFloat(match[1].replace(',', '.'));
  return Number.isFinite(price) ? price : 0;
}

function computeTotal(items: string[], newPrice: number): number {
  const total = items.reduce((sum, item) => sum + parseItemPrice(item), Number.isFinite(newPrice) ? newPrice : 0);
  return Number.isFinite(total) ? total : 0;
}

// Garantit un prix numérique valide (jamais NaN → jamais null en base)
function safePrice(value: string | number | undefined): number {
  const price = typeof value === 'number' ? value : parseFloat(value || '0');
  return Number.isFinite(price) ? price : 0;
}

export async function addOrUpdateProduct(data: ProductInput) {
  try {
    console.log('Ajout/mise à jour du produit:', data.clientNumber);
    
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      const itemDescription = data.note ? `${data.note} ${data.price}€` : `${data.category} ${data.price}€`;
      
      // Rechercher si le produit existe déjà
      const products = await getLocalProducts('draft');
      const existingProduct = products.find(p => p.client_number === data.clientNumber);
      
      if (existingProduct) {
        const currentItems = JSON.parse(existingProduct.items);
        const newItems = [...currentItems, itemDescription];
        const newTotal = computeTotal(currentItems, parseFloat(data.price));
        
        await updateLocalProduct(existingProduct.id!, {
          items: JSON.stringify(newItems),
          total_price: newTotal
        });
      } else {
        await addLocalProduct({
          client_number: data.clientNumber,
          items: JSON.stringify([itemDescription]),
          total_price: safePrice(data.price),
          status: 'draft'
        });
      }
      
      toast.success('Produit ajouté avec succès');
      return true;
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const { data: existingProduct, error: fetchError } = await supabase
        .from(PRODUCTS_TABLE)
        .select()
        .eq('client_number', data.clientNumber)
        .eq('status', 'draft')
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const itemDescription = data.note ? `${data.note} ${data.price}€` : `${data.category} ${data.price}€`;

      if (existingProduct) {
        const currentItems = JSON.parse(existingProduct.items);
        const newItems = [...currentItems, itemDescription];
        const newTotal = computeTotal(currentItems, parseFloat(data.price));

        const { error: updateError } = await supabase
          .from(PRODUCTS_TABLE)
          .update({
            items: JSON.stringify(newItems),
            total_price: newTotal,
            updated_at: new Date().toISOString() // Force explicit timestamp update
          })
          .eq('id', existingProduct.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from(PRODUCTS_TABLE)
          .insert({
            client_number: data.clientNumber,
            items: JSON.stringify([itemDescription]),
            total_price: safePrice(data.price),
            status: 'draft',
            created_at: new Date().toISOString(), // Force explicit timestamp
            updated_at: new Date().toISOString()  // Force explicit timestamp
          });

        if (insertError) throw insertError;
      }

      return true;
    });
  } catch (error) {
    console.error('Erreur dans addOrUpdateProduct:', error);
    toast.error(handleSupabaseError(error));
    return false;
  }
}

export async function getProducts(status: Product['status']) {
  try {
    console.log('Récupération des produits avec statut:', status);
    
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      console.log('Utilisation de la base de données locale');
      return await getLocalProducts(status);
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from(PRODUCTS_TABLE)
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des produits:', error);
        throw error;
      }

      console.log(`${data?.length || 0} produits récupérés`);
      return data || [];
    });
  } catch (error) {
    console.error('Erreur dans getProducts:', error);
    
    // En cas d'erreur avec Supabase, essayer de récupérer les données localement
    if (!useLocalMode) {
      console.log('Tentative de récupération des données localement après échec Supabase');
      try {
        const localProducts = await getLocalProducts(status);
        if (localProducts.length > 0) {
          toast('Utilisation des données locales (mode hors-ligne)', { icon: '⚠️' });
          useLocalMode = true;
          localStorage.setItem('demoMode', 'true');
          return localProducts;
        }
      } catch (localError) {
        console.error('Erreur lors de la récupération des données locales:', localError);
      }
    }
    
    toast.error(handleSupabaseError(error));
    return [];
  }
}

export async function updateProduct(id: number, updates: ProductUpdate) {
  try {
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      const items = typeof updates.items === 'string' ? 
        JSON.parse(updates.items) : 
        updates.items;
      
      await updateLocalProduct(id, {
        client_number: updates.clientNumber,
        items: JSON.stringify(items),
        total_price: safePrice(updates.manualTotal || updates.totalPrice)
      });
      
      return true;
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const items = typeof updates.items === 'string' ? 
        JSON.parse(updates.items) : 
        updates.items;

      const { error } = await supabase
        .from(PRODUCTS_TABLE)
        .update({
          client_number: updates.clientNumber,
          items: JSON.stringify(items),
          total_price: safePrice(updates.manualTotal || updates.totalPrice),
          updated_at: new Date().toISOString() // Force explicit timestamp update
        })
        .eq('id', id);

      if (error) throw error;
      return true;
    });
  } catch (error) {
    console.error('Erreur dans updateProduct:', error);
    toast.error(handleSupabaseError(error));
    return false;
  }
}

export async function updateProductStatus(id: number, status: Product['status'], syncId?: string) {
  try {
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      const updates: Partial<Product> = { status };
      if (syncId) {
        updates.sync_id = syncId;
      }
      
      await updateLocalProduct(id, updates);
      return true;
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const updateData: any = { 
        status,
        updated_at: new Date().toISOString() // Force explicit timestamp update
      };
      
      if (syncId) {
        updateData.sync_id = syncId;
      }
      
      const { error } = await supabase
        .from(PRODUCTS_TABLE)
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      return true;
    });
  } catch (error) {
    console.error('Erreur dans updateProductStatus:', error);
    toast.error(handleSupabaseError(error));
    return false;
  }
}

export async function updateProductSyncStatus(id: number, syncStatus: 'pending' | 'synced' | 'failed', syncId: string, wooId?: number) {
  try {
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      const updates: Partial<Product> = { 
        sync_status: syncStatus,
        sync_id: syncId
      };
      
      if (wooId) {
        updates.id_woo = wooId;
      }
      
      await updateLocalProduct(id, updates);
      return true;
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const updateData: any = { 
        sync_status: syncStatus,
        sync_id: syncId,
        updated_at: new Date().toISOString() // Force explicit timestamp update
      };
      
      if (wooId) {
        updateData.id_woo = wooId;
      }
      
      const { error } = await supabase
        .from(PRODUCTS_TABLE)
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      return true;
    });
  } catch (error) {
    console.error('Erreur dans updateProductSyncStatus:', error);
    toast.error(handleSupabaseError(error));
    return false;
  }
}

export async function getProductsBySyncId(syncId: string) {
  try {
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      const allProducts = await getLocalProducts('completed');
      return allProducts.filter(p => p.sync_id === syncId);
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from(PRODUCTS_TABLE)
        .select('*')
        .eq('sync_id', syncId);

      if (error) throw error;
      return data || [];
    });
  } catch (error) {
    console.error('Erreur dans getProductsBySyncId:', error);
    toast.error(handleSupabaseError(error));
    return [];
  }
}

export async function deleteProduct(id: number) {
  try {
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      await deleteLocalProduct(id);
      toast.success('Produit supprimé avec succès');
      return true;
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const { error } = await supabase
        .from(PRODUCTS_TABLE)
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Produit supprimé avec succès');
      return true;
    });
  } catch (error) {
    console.error('Erreur dans deleteProduct:', error);
    toast.error(handleSupabaseError(error));
    return false;
  }
}

export async function checkProductSyncStatus(id: number) {
  try {
    // Si mode local, utiliser IndexedDB
    if (useLocalMode) {
      const db = await getLocalProducts('completed');
      const product = db.find(p => p.id === id);
      if (!product) return null;
      
      return {
        id_woo: product.id_woo,
        sync_status: product.sync_status,
        sync_id: product.sync_id
      };
    }
    
    // Sinon, utiliser Supabase
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from(PRODUCTS_TABLE)
        .select('id_woo, sync_status, sync_id')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    });
  } catch (error) {
    console.error('Erreur dans checkProductSyncStatus:', error);
    return null;
  }
}

// Fonction pour mettre à jour les champs de date manquants
export async function fixMissingTimestamps() {
  try {
    if (useLocalMode) {
      return; // Ignorer en mode local
    }

    console.log('Vérification et correction des timestamps manquants...');
    
    // Récupérer les produits sans timestamps
    const { data: missingTimestamps, error: fetchError } = await supabase
      .from(PRODUCTS_TABLE)
      .select('id')
      .or('created_at.is.null,updated_at.is.null');
    
    if (fetchError) {
      console.error('Erreur lors de la recherche de timestamps manquants:', fetchError);
      return;
    }
    
    if (!missingTimestamps || missingTimestamps.length === 0) {
      console.log('Aucun timestamp manquant détecté');
      return;
    }
    
    console.log(`${missingTimestamps.length} produits avec timestamps manquants trouvés`);
    
    // Mettre à jour tous les produits avec des timestamps manquants
    const now = new Date().toISOString();
    
    for (const product of missingTimestamps) {
      const { error: updateError } = await supabase
        .from(PRODUCTS_TABLE)
        .update({
          created_at: now,
          updated_at: now
        })
        .eq('id', product.id);
      
      if (updateError) {
        console.error(`Erreur lors de la mise à jour du timestamp pour le produit ${product.id}:`, updateError);
      }
    }
    
    console.log('Correction des timestamps terminée');
    
  } catch (error) {
    console.error('Erreur lors de la correction des timestamps:', error);
  }
}

// Composant React pour l'alerte de connexion
const ConnectionToast = ({ 
  t, 
  onDismiss, 
  onActivate 
}: { 
  t: { id: string }, 
  onDismiss: () => void, 
  onActivate: () => void 
}) => {
  return React.createElement(
    'div', 
    { className: "bg-white rounded-lg shadow-md p-4 max-w-md" },
    React.createElement(
      'p', 
      { className: "font-semibold mb-2" }, 
      "Connexion au serveur disponible"
    ),
    React.createElement(
      'p', 
      { className: "mb-3 text-sm" }, 
      "Vous êtes actuellement en mode hors-ligne. Voulez-vous revenir au mode normal ?"
    ),
    React.createElement(
      'div', 
      { className: "flex justify-end space-x-2" },
      React.createElement(
        'button',
        { 
          onClick: onDismiss, 
          className: "px-3 py-1 bg-gray-200 rounded-md"
        },
        "Rester hors-ligne"
      ),
      React.createElement(
        'button',
        { 
          onClick: onActivate, 
          className: "px-3 py-1 bg-blue-500 text-white rounded-md"
        },
        "Activer"
      )
    )
  );
};

export async function addBulkProduct(data: {
  clientNumbers: string[];
  category: string;
  note: string;
  price: string;
}) {
  try {
    console.log('Ajout en masse du produit pour', data.clientNumbers.length, 'clients');

    const itemDescription = data.note ? `${data.note} ${data.price}€` : `${data.category} ${data.price}€`;
    const price = safePrice(data.price);

    if (useLocalMode) {
      for (const clientNumber of data.clientNumbers) {
        const products = await getLocalProducts('draft');
        const existingProduct = products.find(p => p.client_number === clientNumber);

        if (existingProduct) {
          const currentItems = JSON.parse(existingProduct.items);
          const newItems = [...currentItems, itemDescription];
          const newTotal = computeTotal(currentItems, price);

          await updateLocalProduct(existingProduct.id!, {
            items: JSON.stringify(newItems),
            total_price: newTotal
          });
        } else {
          await addLocalProduct({
            client_number: clientNumber,
            items: JSON.stringify([itemDescription]),
            total_price: price,
            status: 'draft'
          });
        }
      }

      toast.success(`Produit ajouté à ${data.clientNumbers.length} panier(s)`);
      return true;
    }

    return await withRetry(async () => {
      for (const clientNumber of data.clientNumbers) {
        const { data: existingProduct, error: fetchError } = await supabase
          .from(PRODUCTS_TABLE)
          .select()
          .eq('client_number', clientNumber)
          .eq('status', 'draft')
          .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
          throw fetchError;
        }

        if (existingProduct) {
          const currentItems = JSON.parse(existingProduct.items);
          const newItems = [...currentItems, itemDescription];
          const newTotal = computeTotal(currentItems, price);

          const { error: updateError } = await supabase
            .from(PRODUCTS_TABLE)
            .update({
              items: JSON.stringify(newItems),
              total_price: newTotal,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingProduct.id);

          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from(PRODUCTS_TABLE)
            .insert({
              client_number: clientNumber,
              items: JSON.stringify([itemDescription]),
              total_price: price,
              status: 'draft',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (insertError) throw insertError;
        }
      }

      toast.success(`Produit ajouté à ${data.clientNumbers.length} panier(s)`);
      return true;
    });
  } catch (error) {
    console.error('Erreur dans addBulkProduct:', error);
    toast.error(handleSupabaseError(error));
    return false;
  }
}

// Fonction pour vérifier et basculer entre les modes
export async function checkAndSwitchMode() {
  if (useLocalMode) {
    // Si on est en mode local, vérifier si Supabase est disponible
    try {
      const isConnected = await validateConnection();
      if (isConnected) {
        // Proposer de revenir en mode normal
        toast.custom((t) => (
          React.createElement(ConnectionToast, {
            t: t,
            onDismiss: () => {
              toast.dismiss(t.id);
            },
            onActivate: () => {
              setUseLocalMode(false);
              toast.success('Mode normal activé');
              toast.dismiss(t.id);
            }
          })
        ), { duration: 10000 });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de la connexion Supabase:', error);
    }
  }
}